import { useState, useRef, useCallback, useEffect } from 'react';
import { useSession } from '@/features/auth/hooks/useSession';
import { listObjects } from '@/lib/api/endpoints/objects';
import { extractContactValue } from '@/modules/contacts/contactValue';

interface Attendee {
  email: string;
  /** Canonical display name (events synced from any provider store it here, #3998). */
  name?: string;
  /** Legacy Google-API spelling — read as a fallback for older/manual events. */
  displayName?: string;
  responseStatus?: string;
  photoUrl?: string;
  organizer?: boolean;
  self?: boolean;
}

interface AttendeeChipsProps {
  attendees: Attendee[];
  onChange: (attendees: Attendee[]) => void;
  sendInvitations: boolean;
  onSendInvitationsChange: (value: boolean) => void;
  viewOnly?: boolean;
}

const RESPONSE_BADGES: Record<string, { label: string; color: string }> = {
  accepted: { label: 'Yes', color: '#33B679' },
  declined: { label: 'No', color: '#D50000' },
  tentative: { label: 'Maybe', color: '#F4511E' },
  needsAction: { label: 'Pending', color: '#9E9E9E' },
};

export function AttendeeChips({ attendees, onChange, sendInvitations, onSendInvitationsChange, viewOnly }: AttendeeChipsProps) {
  const { token } = useSession();
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<Array<{ title: string; email: string }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards async suggestion results against setState-after-unmount (#4197):
  // closing the modal while a debounced lookup is in-flight must not crash.
  const isMountedRef = useRef(true);

  // Cancel any pending debounced lookup and stop late async results from
  // calling setState once the component unmounts (e.g. modal closed mid-type).
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const searchContacts = useCallback(async (query: string) => {
    if (!token || query.length < 2) {
      if (isMountedRef.current) setSuggestions([]);
      return;
    }
    try {
      const result = await listObjects(token, { type: 'contact', query, limit: 10 });
      if (!isMountedRef.current) return;
      const items = (result?.items || [])
        .map((c: any) => {
          // listObjects runs transformObject, which emits a canonical
          // `emails: string[]` plus a top-level `email: string`. Either may be a
          // raw `{ value }` element, so unwrap to a string — rendering that
          // object as a React child throws and the key/addAttendee paths would
          // collide/TypeError (#4906).
          const email =
            extractContactValue(c.emails?.[0]) ||
            extractContactValue(c.email);
          return { title: c.title || c.name, email };
        })
        .filter((s: { email: string }) => !!s.email);
      setSuggestions(items);
      setShowSuggestions(items.length > 0);
    } catch (err) {
      console.error('[AttendeeChips] Search failed:', err);
      if (isMountedRef.current) setSuggestions([]);
    }
  }, [token]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setSelectedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchContacts(value), 300);
  };

  const addAttendee = (email: string, displayName?: string) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || attendees.some(a => a.email === trimmed)) return;
    onChange([...attendees, { email: trimmed, displayName: displayName || undefined }]);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeAttendee = (email: string) => {
    onChange(attendees.filter(a => a.email !== email));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        const s = suggestions[selectedIndex];
        addAttendee(s.email, s.title);
      } else if (inputValue.includes('@')) {
        addAttendee(inputValue);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && !inputValue && attendees.length > 0) {
      removeAttendee(attendees[attendees.length - 1].email);
    }
  };

  return (
    <div className="attendee-chips-container">
      <div className="attendee-chips-list">
        {attendees.map(a => (
          <span key={a.email} className="attendee-chip">
            <span className="attendee-chip-text">
              {a.name || a.displayName || a.email}
            </span>
            {a.responseStatus && RESPONSE_BADGES[a.responseStatus] && (
              <span
                className="attendee-response-badge"
                style={{ color: RESPONSE_BADGES[a.responseStatus].color }}
              >
                {RESPONSE_BADGES[a.responseStatus].label}
              </span>
            )}
            {!viewOnly && (
              <button
                type="button"
                className="attendee-chip-remove"
                onClick={() => removeAttendee(a.email)}
                title={`Remove ${a.email}`}
              >
                &times;
              </button>
            )}
          </span>
        ))}

        {!viewOnly && (
          <div className="attendee-input-wrapper">
            <input
              ref={inputRef}
              type="email"
              className="attendee-input"
              placeholder={attendees.length === 0 ? 'Add attendee email...' : 'Add another...'}
              value={inputValue}
              onChange={e => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            />

            {showSuggestions && (
              <div className="attendee-suggestions">
                {suggestions.map((s, i) => (
                  <button
                    key={s.email}
                    type="button"
                    className={`attendee-suggestion ${i === selectedIndex ? 'selected' : ''}`}
                    onMouseDown={() => addAttendee(s.email, s.title)}
                  >
                    <span className="suggestion-name">{s.title}</span>
                    <span className="suggestion-email">{s.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!viewOnly && attendees.length > 0 && (
        <label className="attendee-send-toggle">
          <input
            type="checkbox"
            checked={sendInvitations}
            onChange={e => onSendInvitationsChange(e.target.checked)}
          />
          <span>Send email invitations</span>
        </label>
      )}
    </div>
  );
}
